import { useFormik } from 'formik';
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { setItem } from '../../../utils/localStorage';
import { authenticateUser } from '../../../store/auth/api';
import { login } from '../../../store/auth/authSlice';
import { clone, set } from 'lodash';
import Iconify from '../../../components/Iconify';
import { useState, useEffect, useRef } from 'react';
import { validateEmail, validatePhoneNumber } from '../../../utils/validation';
import { SuperUserRole } from '../../../enum';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { COUNTRY_CODE } from '../../../constants';
import { createCompanyOtpLogin } from '../../../store/companyOtpLogin/api';
import { detectUserCountry, findCountryCallingCode } from '../../../utils/countryDetection';
import * as Yup from 'yup';

// Validation schema for email/password login
const EmailPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Email must be valid email address')
    .required('Email is required'),
  password: Yup.string().required('Password is required'),
});

// Validation schema for mobile number (OTP login)
const MobileSchema = Yup.object().shape({
  countryCallingCode: Yup.string().required('Country code is required'),
  mobileNumber: Yup.string()
    .required('Mobile number is required')
    .matches(/^[0-9]{7,15}$/, 'Invalid mobile number'),
});

const CompanyLoginForm = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isOtpMode, setIsOtpMode] = useState<boolean | null>(null); // null = not determined, true = OTP, false = email
  const [otpSent, setOtpSent] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const hasManuallyChangedCountryCode = useRef(false);
  const hasAutoDetected = useRef(false);

  const {
    handleSubmit,
    resetForm,
    isSubmitting,
    values,
    touched,
    errors,
    setFieldValue,
    getFieldProps,
  } = useFormik({
    initialValues: {
      emailOrPhone: '',
      countryCallingCode: '+974',
      mobileNumber: '',
      email: '',
      password: '',
      strategy: 'local',
      otp: '',
    },
    validationSchema: isOtpMode === false ? EmailPasswordSchema : isOtpMode === true ? MobileSchema : null,
    onSubmit: async () => {
      try {
        // If OTP mode and OTP not sent yet, send OTP first
        if (isOtpMode === true && !otpSent) {
          const otpResponse = await dispatch(
            createCompanyOtpLogin({
              countryCallingCode: values.countryCallingCode,
              mobileNumber: values.mobileNumber,
            })
          ).unwrap();

          if (otpResponse?.requestId) {
            setRequestId(otpResponse.requestId);
            setOtpSent(true);
            toast.success('OTP sent to your mobile number');
            return;
          }
        }

        // If OTP sent, verify OTP
        if (isOtpMode === true && otpSent && requestId) {
          if (!values.otp || values.otp.length < 4) {
            toast.error('Please enter a valid OTP');
            return;
          }

          const otpValues = {
            requestId: requestId,
            otp: values.otp,
            strategy: 'otp',
          };

          const res = await dispatch(authenticateUser(otpValues as any)).unwrap();
          
          if (res?.accessToken) {
            // Validate company role and account status
            if (res.user.userRole !== SuperUserRole.company) {
              toast.error('Only Company Admin accounts can log in here');
              return;
            }

            if (res.user.activeState === false) {
              toast.error('Your account is suspended. Please contact support.');
              return;
            }

            if (res.user.isApproved === false || res.user.isApproved === null) {
              toast.error('Your account is pending approval');
              return;
            }

            setItem('info', res?.user);
            // Store the user role in localStorage for logout redirect
            localStorage.setItem('lastUserRole', res.user.userRole);
            dispatch(login({ accessToken: res?.accessToken, user: res?.user }));
            resetForm();
            navigate('/company-dashboard');
          }
          return;
        }

        // Email/Password login
        if (isOtpMode === false) {
          const updatedValues = set(
            clone(values),
            'email',
            values.email.toLowerCase()
          );
          const res = await dispatch(authenticateUser(updatedValues)).unwrap();

          if (res?.accessToken) {
            // Validate company role and account status
            if (res.user.userRole !== SuperUserRole.company) {
              toast.error('Only Company Admin accounts can log in here');
              return;
            }

            if (res.user.activeState === false) {
              toast.error('Your account is suspended. Please contact support.');
              return;
            }

            if (res.user.isApproved === false || res.user.isApproved === null) {
              toast.error('Your account is pending approval');
              return;
            }

            setItem('info', res?.user);
            // Store the user role in localStorage for logout redirect
            localStorage.setItem('lastUserRole', res.user.userRole);
            dispatch(login({ accessToken: res?.accessToken, user: res?.user }));
            resetForm();
            navigate('/company-dashboard');
          }
        }
      } catch (error: any) {
        // Error handling is done by toastHandler in the API
      }
    },
  });

  // Detect user's country code only once on component mount
  // Only auto-detect if user hasn't manually changed it and we haven't already detected
  useEffect(() => {
    if (!hasAutoDetected.current && !hasManuallyChangedCountryCode.current && values.countryCallingCode === '+974') {
      const detected = detectUserCountry();
      const countryCode = findCountryCallingCode(detected.callingCode, detected.isoCode);
      // Only set if detected code is different from default and seems reasonable
      if (countryCode && countryCode !== '+974') {
        setFieldValue('countryCallingCode', countryCode);
        hasAutoDetected.current = true;
      }
    }
  }, [setFieldValue, values.countryCallingCode]);

  const handleEmailOrPhoneChange = (value: string) => {
    setFieldValue('emailOrPhone', value);
    
    // Detect input type
    if (value.trim() === '') {
      setIsOtpMode(null);
      return;
    }

    if (validateEmail(value.trim())) {
      setIsOtpMode(false);
      setFieldValue('email', value.trim());
      setFieldValue('mobileNumber', '');
      setOtpSent(false);
      setRequestId(null);
    } else if (validatePhoneNumber(value.trim())) {
      setIsOtpMode(true);
      const cleanedNumber = value.trim().replace(/[\s\-\(\)]/g, '');
      setFieldValue('mobileNumber', cleanedNumber);
      setFieldValue('email', '');
      setOtpSent(false);
      setRequestId(null);
    } else {
      setIsOtpMode(null);
    }
  };

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleResendOTP = async () => {
    if (isOtpMode === true) {
      try {
        const otpResponse = await dispatch(
          createCompanyOtpLogin({
            countryCallingCode: values.countryCallingCode,
            mobileNumber: values.mobileNumber,
          })
        ).unwrap();

        if (otpResponse?.requestId) {
          setRequestId(otpResponse.requestId);
          setOtpSent(true);
          toast.success('OTP resent to your mobile number');
        }
      } catch (error: any) {
        // Error handled by toastHandler
      }
    }
  };

  return (
    <form
      autoComplete='off'
      noValidate
      onSubmit={handleSubmit}
    >
      <Stack spacing={3}>
        {/* Main Input Field - Email or Phone (show when not detected or when email mode) */}
        {(isOtpMode === null || isOtpMode === false) && (
          <>
          <TextField
            fullWidth
            autoComplete='username'
            type={isOtpMode === false ? 'email' : 'text'}
            label={isOtpMode === false ? 'Email Address' : 'Email or Mobile Number'}
            value={isOtpMode === false ? values.email : values.emailOrPhone}
            onChange={(e) => {
              if (isOtpMode === false) {
                const emailValue = e.target.value;
                setFieldValue('email', emailValue);
                setFieldValue('emailOrPhone', emailValue);
              } else {
                handleEmailOrPhoneChange(e.target.value);
              }
            }}
            error={
              isOtpMode === false
                ? Boolean(errors.email && touched.email)
                : Boolean(values.emailOrPhone.trim() !== '' && touched.emailOrPhone && isOtpMode === null)
            }
            helperText={
              isOtpMode === false
                ? Boolean(errors.email && touched.email) && errors.email
                : values.emailOrPhone.trim() !== '' && isOtpMode === null
                ? 'Please enter a valid email or mobile number'
                : ''
            }
          />
            {/* Show Forgot Password link on initial screen (when email/mobile input is shown but password is not) */}
            {isOtpMode === null && (
              <Stack direction='row' justifyContent='flex-end' sx={{ mt: -1, width: '100%' }}>
                <Link
                  component='button'
                  type='button'
                  variant='body2'
                  onClick={() => {
                    // Store that user came from company login
                    localStorage.setItem('resetPasswordFromCompanyLogin', 'true');
                    navigate('/reset-password');
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  Forgot Password?
                </Link>
              </Stack>
            )}
          </>
        )}

        {/* OTP Mode - Show Country Code and Mobile Number */}
        {isOtpMode === true && (
          <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ flex: '0 0 calc(33.333% - 8px)', minWidth: 0 }}>
                <FormControl
                  fullWidth
                  error={Boolean(touched.countryCallingCode && errors.countryCallingCode)}
                  variant="outlined"
                >
                  <InputLabel>Country Code</InputLabel>
                  <Select
                    name='countryCallingCode'
                    label='Country Code'
                    value={values.countryCallingCode}
                    onChange={(e) => {
                      setFieldValue('countryCallingCode', e.target.value);
                      hasManuallyChangedCountryCode.current = true;
                    }}
                    MenuProps={{
                      style: {
                        maxHeight: 400,
                      },
                    }}
                  >
                    {COUNTRY_CODE?.map((country) => (
                      <MenuItem key={country.name} value={country.code.trim()}>
                        {country.code} {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText sx={{ margin: 0, minHeight: '1.4375em' }}>
                    {Boolean(touched.countryCallingCode && errors.countryCallingCode) 
                      ? errors.countryCallingCode 
                      : '\u00A0'}
                  </FormHelperText>
                </FormControl>
              </Box>
              <Box sx={{ flex: '0 0 calc(66.666% - 8px)', minWidth: 0 }}>
                <TextField
                  fullWidth
                  label='Mobile Number'
                  name='mobileNumber'
                  value={values.mobileNumber}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9]/g, '');
                    setFieldValue('mobileNumber', cleaned);
                  }}
                  error={Boolean(touched.mobileNumber && errors.mobileNumber)}
                  helperText={
                    Boolean(touched.mobileNumber && errors.mobileNumber) 
                      ? errors.mobileNumber 
                      : '\u00A0'
                  }
                  disabled={otpSent}
                  variant="outlined"
                  sx={{
                    '& .MuiFormHelperText-root': {
                      margin: 0,
                      minHeight: '1.4375em',
                    },
                  }}
                />
              </Box>
            </Box>

            {/* OTP Input - Show after OTP is sent */}
            {otpSent && (
              <>
                <TextField
                  fullWidth
                  label='Enter OTP'
                  name='otp'
                  value={values.otp}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                    setFieldValue('otp', cleaned);
                  }}
                  inputProps={{ maxLength: 6 }}
                  helperText='We sent a verification code to your mobile number'
                />
                <Stack direction='row' justifyContent='flex-end' sx={{ mt: 1 }}>
                  <LoadingButton
                    variant='text'
                    onClick={handleResendOTP}
                    disabled={isSubmitting}
                  >
                    Resend OTP
                  </LoadingButton>
                </Stack>
              </>
            )}
          </>
        )}

        {/* Email/Password Mode - Show Password Field */}
        {isOtpMode === false && (
          <TextField
            fullWidth
            autoComplete='current-password'
            type={showPassword ? 'text' : 'password'}
            label='Password'
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={handleShowPassword}
                    edge='end'
                  >
                    <Iconify
                      icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(errors.password && touched.password)}
            helperText={
              Boolean(errors.password && touched.password) && errors.password
            }
          />
        )}

        {/* Show Forgot Password link below password field (only when email mode is active) */}
        {isOtpMode === false && (
          <Stack direction='row' justifyContent='flex-end' sx={{ mt: -1, width: '100%' }}>
            <Link
              component='button'
              type='button'
              variant='body2'
              onClick={() => {
                // Store that user came from company login
                localStorage.setItem('resetPasswordFromCompanyLogin', 'true');
                navigate('/reset-password');
              }}
              sx={{ cursor: 'pointer' }}
            >
              Forgot Password?
            </Link>
          </Stack>
        )}
      </Stack>

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='center'
        sx={{ mt: 3 }}
      >
        <LoadingButton
          fullWidth
          size='large'
          type='submit'
          variant='contained'
          loading={isSubmitting}
          disabled={isOtpMode === null && values.emailOrPhone.trim() !== ''}
        >
          {isOtpMode === true && otpSent ? 'Verify OTP' : 'Continue'}
        </LoadingButton>
      </Stack>
    </form>
  );
};

export default CompanyLoginForm;

